// TemperatureSensorDashboard.js (alphatech)
// Standalone Temperature Sensor dashboard. Same data as Aquabrim's
// TemperatureSensorPage (temperatureSensorData.mainData + functionalPoints),
// rendered in alphatech's flat slate style with recharts trend lines.
import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { FaThermometerHalf, FaArrowUp, FaArrowDown, FaMinus } from 'react-icons/fa';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { SLATE, SectionCard, cardShell } from './sensorUi';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const IST = 'Asia/Kolkata';
const HEAT = '#f97316';

const pctFor = (lvl, max) => (typeof lvl === 'number' && lvl > 0 && lvl <= max ? (lvl / max) * 100 : null);

// Temperature bar: 0–100°C with low/high functional markers.
const TempBar = ({ value, low, high, max = 100 }) => {
  const pct = Math.max(0, Math.min((value / max) * 100, 100));
  const lowPct = pctFor(low, max);
  const highPct = pctFor(high, max);
  return (
    <div style={{ position: 'relative', height: 34, borderRadius: 999, background: '#eef2f7', border: `1px solid ${SLATE.line}`, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, borderRadius: 999, background: `linear-gradient(90deg, #fbbf24 0%, #f97316 60%, #ef4444 100%)`, transition: 'width 0.5s ease' }} />
      {lowPct !== null && <div title={`Low: ${low}`} style={{ position: 'absolute', top: 3, bottom: 3, left: `${lowPct}%`, width: 3, borderRadius: 2, background: '#dc2626' }} />}
      {highPct !== null && <div title={`High: ${high}`} style={{ position: 'absolute', top: 3, bottom: 3, left: `${highPct}%`, width: 3, borderRadius: 2, background: '#1d4ed8' }} />}
    </div>
  );
};

const Trend = ({ flow, rate }) => {
  let Icon = FaMinus; let color = SLATE.muted;
  if (flow === 'upward') { Icon = FaArrowUp; color = '#dc2626'; }
  else if (flow === 'downward') { Icon = FaArrowDown; color = '#2563eb'; }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color }}>
      <Icon size={11} />
      <span style={{ fontWeight: 700, fontSize: '0.72rem' }}>{rate ?? 0}</span>
    </span>
  );
};

// Today's logs → [{ time, value }] in IST.
const toTodaySeries = (logs) => {
  const today = dayjs().tz(IST).format('YYYY-MM-DD');
  return (logs || [])
    .filter((l) => dayjs.utc(l.timestamp).tz(IST).format('YYYY-MM-DD') === today)
    .map((l) => ({ time: dayjs.utc(l.timestamp).tz(IST).format('HH:mm'), value: Number(l.value) }));
};

const TempTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: `1px solid ${SLATE.line}`, borderRadius: 10, boxShadow: '0 6px 18px rgba(15,23,42,0.12)', padding: '6px 10px' }}>
      <div style={{ fontWeight: 800, color: SLATE.ink, fontSize: '0.72rem' }}>{label}</div>
      <div style={{ color: HEAT, fontWeight: 800, fontSize: '0.72rem' }}>{Number(payload[0].value).toFixed(1)} °C</div>
    </div>
  );
};

const SensorCard = ({ sensor }) => {
  const data = sensor.temperatureSensorData || {};
  const main = data.mainData || {};
  const fp = data.functionalPoints || {};
  const value = Number(main.presentTemperature) || 0;
  const updatedAt = data.updated_at;
  const stale = updatedAt ? dayjs().diff(dayjs(updatedAt), 'minute') > 30 : true;

  return (
    <div style={{ ...cardShell, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, color: SLATE.ink, fontSize: '0.95rem' }}>{sensor.sensor_name}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: stale ? 'radial-gradient(#ff4b4b,#aa0000)' : 'radial-gradient(#2ecc71,#27ae60)' }} />
              <span style={{ fontSize: '0.68rem', fontWeight: 600, color: stale ? '#dc2626' : SLATE.muted }}>
                {updatedAt ? dayjs(updatedAt).fromNow() : 'No data'}
              </span>
            </span>
            <Trend flow={main.flow} rate={main.flowRate} />
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ fontSize: '1.9rem', fontWeight: 800, color: HEAT, lineHeight: 1, letterSpacing: '-1px' }}>{value.toFixed(1)}</span>
          <span style={{ marginLeft: 4, fontWeight: 700, color: SLATE.faint, fontSize: '0.85rem' }}>°C</span>
        </div>
      </div>

      <TempBar value={value} low={fp.lowLevel} high={fp.highLevel} />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontSize: '0.62rem', color: '#b0bbc9', fontWeight: 600 }}>0°C</span>
        <span style={{ fontSize: '0.62rem', color: '#b0bbc9', fontWeight: 600 }}>100°C</span>
      </div>
    </div>
  );
};

const TemperatureSensorDashboard = ({ sensors }) => {
  const list = sensors || [];

  if (list.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: SLATE.faint, fontSize: '0.9rem' }}>
        No temperature sensors are configured on this device.
      </div>
    );
  }

  const withLogs = list.filter((s) => s.temperatureSensorData?.mainData?.presentTemperatureLogs?.length);

  return (
    <div>
      <SectionCard
        icon={<FaThermometerHalf size={16} color={HEAT} />}
        title="Temperature Sensors"
        subtitle={`${list.length} sensor${list.length > 1 ? 's' : ''}`}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
          {list.map((s, i) => <SensorCard key={s._id || i} sensor={s} />)}
        </div>
      </SectionCard>

      {withLogs.map((s) => {
        const series = toTodaySeries(s.temperatureSensorData.mainData.presentTemperatureLogs);
        return (
          <SectionCard key={`g-${s._id}`} icon={<FaThermometerHalf size={16} color={HEAT} />} title={`${s.sensor_name} — Today`} subtitle="°C">
            {series.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={series} margin={{ top: 12, right: 16, left: -8, bottom: 6 }}>
                  <defs>
                    <linearGradient id={`heat-${s._id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={HEAT} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={HEAT} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 4" stroke={SLATE.line2} vertical={false} />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: SLATE.muted }} tickMargin={6} axisLine={{ stroke: SLATE.line }} tickLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: SLATE.muted }} width={34} axisLine={false} tickLine={false} domain={[(min) => Math.floor(min - 2), (max) => Math.ceil(max + 2)]} />
                  <Tooltip content={<TempTooltip />} />
                  <Area type="monotone" dataKey="value" stroke={HEAT} strokeWidth={2.2} fill={`url(#heat-${s._id})`} dot={false} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: SLATE.faint, fontSize: '0.82rem' }}>
                No readings recorded today.
              </div>
            )}
          </SectionCard>
        );
      })}
    </div>
  );
};

export default TemperatureSensorDashboard;
